import ShowDetail from '@/components/ShowDetail/ShowDetail';
import api from '@/shared/api/api';
import { BodyExecuteTestCaseRunApiTestRunsExecuteTestCaseRunIdPut, HTTPValidationError, TestCaseResponseDTO, TestCaseRunResponseDTO, TestCaseRunResult } from '@/shared/api/gen/data-contracts';
import { HttpResponse } from '@/shared/api/gen/http-client';
import { useFetch } from '@/shared/hooks/useFetch';
import Button from '@/shared/ui/Buttons/Button';
import { RictTextField, TextField } from '@/shared/ui/Fields/Fields';
import Form from '@/shared/ui/Form/Form';
import { MultiFileInput, AttachmentItem } from '@/shared/ui/Inputs/FileInput/MultiFileInput';
import SelectInput from '@/shared/ui/Inputs/SelectInput';
import { RichTextInput } from '@/shared/ui/Inputs/StringInput';
import { FlexColumn } from '@/shared/ui/Layouts/Flex';
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';

interface ITestRunForm {
    defaultData: Partial<BodyExecuteTestCaseRunApiTestRunsExecuteTestCaseRunIdPut>;
    onSubmit: (data: BodyExecuteTestCaseRunApiTestRunsExecuteTestCaseRunIdPut) => any;
    testCaseRunID: number;
}

function TestCaseRunForm({ defaultData, onSubmit, testCaseRunID }: ITestRunForm) {
    const { data: testCaseRun } = useFetch<TestCaseRunResponseDTO>(
        () => api.test_runs.getCaseTestRunDetailApiTestRunsTestCasesIdGet(Number(testCaseRunID)),
        {} as any,
        [testCaseRunID]
    );
    const { data: testCase } = useFetch<TestCaseResponseDTO>(
        () =>
            testCaseRun?.test_case_ref
                ? api.test_cases.getTestcasesDetailApiTestCasesIdGet(Number(testCaseRun.test_case_ref))
                : Promise.resolve({data: {}} as HttpResponse<TestCaseResponseDTO, HTTPValidationError>),
        {} as any,
        [testCaseRun.test_case_ref]
    );

    const { register, handleSubmit, reset, control } = useForm<BodyExecuteTestCaseRunApiTestRunsExecuteTestCaseRunIdPut>({
        defaultValues: { ...(testCaseRun as BodyExecuteTestCaseRunApiTestRunsExecuteTestCaseRunIdPut), ...(defaultData || {}) },
    });

    const [attachments, setAttachments] = useState<AttachmentItem[]>([]);

    React.useEffect(() => {
        reset(defaultData, { keepDirty: true, keepTouched: true });
    }, [defaultData]);

    useEffect(() => {
        const incoming = (testCaseRun as any)?.attachments;
        if (Array.isArray(incoming)) {
            setAttachments(incoming);
        }
    }, [testCaseRun]);

    const handleAddAttachment = async (file: File): Promise<void> => {
        const result = await api.test_runs.addTestCaseRunAttachmentApiTestRunsTestCasesIdAttachmentsPost(
            testCaseRunID,
            { file }
        );
        if (result?.data) {
            setAttachments(prev => [...prev, result.data as AttachmentItem]);
        }
    };

    const handleRemoveAttachment = async (id: number): Promise<void> => {
        await api.test_runs.deleteTestCaseRunAttachmentApiTestRunsTestCasesIdAttachmentsAttachmentIdDelete(
            testCaseRunID,
            id
        );
        setAttachments(prev => prev.filter(a => a.id !== id));
    };

    return (
        <FlexColumn style={{ width: '100%' }}>
            <h1 className='text-24' style={{ alignSelf: 'flex-start' }}>Выполнение тест-кейса</h1>
            <ShowDetail title="" record={testCase}>
                <TextField key="title" label='Название тест-кейса' />
                <TextField key="description" label='Описание тест-кейса' />
                <RictTextField key="precondition" label='Предварительные условия тест-кейса' />
                <RictTextField key="steps" label='Шаги воспроизведения тест-кейса' />
                <RictTextField key="postcondition" label='Действия после завершения тест-кейса' />
            </ShowDetail>

            <Form onSubmit={handleSubmit(onSubmit)} style={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
                <h2 className='text-24'>Результаты тестирования</h2>
                <p>Укажи данные по результатам тестирования</p>
                <SelectInput label='Статус результата теста' register={register('status')} choices={[
                    { value: TestCaseRunResult.WAITING, title: 'Ожидает выполнения' },
                    { value: TestCaseRunResult.SUCCESS, title: 'Успешно завершен' },
                    { value: TestCaseRunResult.SKIPPED, title: 'Пропущен' },
                    { value: TestCaseRunResult.ERROR, title: 'Не удалось провести тест' },
                    { value: TestCaseRunResult.FAILED, title: 'Тест провален' },
                ]} />
                <RichTextInput
                    label="Описание результата тестирования"
                    name="description"
                    style={{ minWidth: '200px' }}
                    control={control}
                />
                <div>
                    <p className='text-15'>Вложения результатов</p>
                    <MultiFileInput
                        attachments={attachments}
                        onAdd={handleAddAttachment}
                        onRemove={handleRemoveAttachment}
                    />
                </div>
                <div className='FormRow'>
                    <Button type='submit'>Завершить тестирование</Button>
                </div>
            </Form>
        </FlexColumn>
    );
}

export default TestCaseRunForm;
